import { set } from 'lodash';
import { useEffect } from 'react';

import { IconHeadline } from '@hivebrite/design-system/Icon';

import {
  HeadlineBuilderWrapper,
  HeadlineWrapper,
} from 'hb-builder/components/BuilderComponents/single/Headline/components/HeadlineWrappers';
import {
  convertResponsivenessValues,
  fontSizes,
  generateResponsiveValues,
  lineHeights,
} from 'hb-builder/components/BuilderComponents/single/Headline/utils/settings';
import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import Drop from 'hb-builder/components/common/Drop';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import { loadFont } from 'hb-builder/utils/fonts';
import { getContent } from 'hb-builder/utils/item';
import { getCustomFontName } from 'hb-react/backoffice/components/PageBuilder/utils/getCustomFontName';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { HeadlineItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/headlineItemAttributes';

import HeadlineSettings from './HeadlineSettings';

const componentType = 'single';

const Headline: BuilderComponent<HeadlineItemAttributes> = () => <div />; // never used

const RenderBuilder = ({ children }) => {
  const { getPath, updateItem } = useBuilderContext();
  const { item } = useComponentContext();
  const {
    hivebrite: { networkFont, networkTheming },
  } = useBuilderConfigContext();

  const attributes = item?.attributes as HeadlineItemAttributes;

  useEffect(() => {
    const customFontName = getCustomFontName(networkTheming);

    if (networkFont && networkFont !== customFontName) {
      loadFont(networkFont);
    }
  }, [networkFont, networkTheming]);

  const initialValue =
    typeof children === 'string' ? getContent(children) : children;

  const handleChangeTextEditor = (value) =>
    updateItem({
      path: getPath(item.id),
      updater: (item) => set(item, 'content', value),
    });

  const textProps = {
    initialValue,
    isHeadline: true,
    onChange: handleChangeTextEditor,
  };

  return (
    <Drop accept={['single']}>
      {({ ref: dropRef }) => (
        <BuilderThemeProvider viewAs={VIEW_AS.USER}>
          <HeadlineBuilderWrapper
            attributes={attributes}
            dropRef={dropRef}
            textProps={textProps}
          />
        </BuilderThemeProvider>
      )}
    </Drop>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<HeadlineItemAttributes>>) => {
  const { attributes } = item || {};

  return (
    <HeadlineWrapper
      attributes={attributes}
      children={children}
      componentType={componentType}
    />
  );
};

Headline.componentName = 'Headline';
Headline.componentType = componentType;
Headline.componentIcon = () => IconHeadline;

Headline.componentLabel = __('Headline');
Headline.componentCategory = 'single';

Headline.canBeDoubleClicked = false;
Headline.canBeDragged = true;
Headline.canBeListed = true;
Headline.canBeSelected = true;

// Default font size 48px & line height 58px
const defaultFontSize = fontSizes[1] as number;
const defaultLineHeight = lineHeights[1] as number;
const responsiveFontsize = convertResponsivenessValues(defaultFontSize);
const responsiveLineHeight = convertResponsivenessValues(defaultLineHeight);
const defaultConfig = {
  containerPadding: '10px 10px 10px 10px',
  content: `<h1 style="text-align: center;">${__(
    'Write your headline here',
  )}</h1>`,
  // Force all breakpoints because object is used in inline style inside WrapperBuilder & Wrapper
  headlineFontSize: generateResponsiveValues({
    s: responsiveFontsize,
    xl: defaultFontSize,
  }),
  headlineLineHeight: generateResponsiveValues({
    s: responsiveLineHeight,
    xl: defaultLineHeight,
  }),
  isCustomMobileValue: false,
};

Headline.item = ({ features }) => {
  const headlineConfig = {
    ...defaultConfig,
    ...(features?.headline || {}),
  };

  return {
    attributes: {
      containerPadding: headlineConfig.containerPadding,
      textFontFamily: headlineConfig.fontFamily,
      headlineFontSize: headlineConfig.headlineFontSize,
      headlineLineHeight: headlineConfig.headlineLineHeight,
      isCustomMobileValue: headlineConfig.isCustomMobileValue,
    },
    category: Headline.componentCategory,
    content: headlineConfig.content,
    isMobileSettings: true,
    name: Headline.componentName,
    type: Headline.componentName,
  };
};

Headline.toolbar = ['edit', 'drag', 'duplicate', 'delete'];
Headline.toolbarMobile = ['edit'];

Headline.Settings = HeadlineSettings;
Headline.SettingsTabs = defaultTabs;

Headline.RenderBuilder = RenderBuilder;
Headline.Render = Render;

export default Headline;
